import {
  type CanvasElementFrom,
  type IoConfig,
  type MultipleColumnsPath,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type IPropEditorResolver,
  type PropertiesConfig,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { MapComponent } from './map.component';
import {
  DEFAULT_MAP_COORDS,
  InitialMapLocation,
  type MapFieldType,
  type MapPropsModel,
  MapStyle,
  MapType,
  MarkerStyle,
} from './map.models';
import { MapPropsBuilder } from './map.props-builder';
import { MapPropsExtractor } from './map.props-extractor';
import { mapV2Migration } from './migrations/002-map.migration';

export type MapDef = WithProps<MapPropsModel> & WithIoConfig;
export type MapElement = CanvasElementFrom<'map'>;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    map: MapDef;
  }
}

registerElementDefinition({
  type: 'map',
  componentType: MapComponent,
  isListed: true,
  name: 'Map',
  width: { mode: 'value', value: 600, fixed: false },
  height: { mode: 'value', value: 400, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: MapPropsBuilder,
  propsExtractor: MapPropsExtractor,
  initial: getInitials(),
  defaults: getDefaults(),
  groups: ['advanced'],
  toolbar: ['triggerClick'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  metaUiFeatures: {
    type: 'container',
    paths: {
      columns: <MultipleColumnsPath<MapFieldType>>{
        card: 'content.card.customFields',
        marker: 'content.markers.customFields',
      },
    },
  },
  features: {
    visualStyle: 'block',
  },
  version: 2,
  migrations: {
    2: mapV2Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'action', schema: DEFAULT_SCHEMA }],
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('map', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Source',
        [],
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Source',
            sources: 'list',
            bindings: [],
          }),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'content.input.coordinates',
                'Coordinates',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnDataTypeFilter('GeoPoint'),
                  tooltip:
                    'Only columns with Map coordinates data type or formulas returning geographic coordinates are visible.',
                },
              ),
            ],
            'data.?key:value.dataSource',
            (value) => !value,
          ),
        ],
        { enabledByDefault: true },
      ),
      epc.subsections.subsection(
        'content.options',
        'Map Options',
        [
          epc.fields.selectFieldFromList(
            'content.options.type',
            'Type',
            [
              { label: 'Road', value: MapType.Road },
              { label: 'Satellite', value: MapType.Satellite },
              { label: 'Terrain', value: MapType.Terrain },
              { label: 'Hybrid', value: MapType.Hybrid },
            ],
            { allowNone: false },
          ),
          epc.utils.separator('Initial Map Location'),
          epc.fields.radiosField(
            'content.location.initial.type',
            '',
            [
              { label: 'Choose Location', value: InitialMapLocation.Selection },
              { label: 'Current Location', value: InitialMapLocation.Current },
            ],
            { hideLabel: true },
          ),
          epc.utils.conditionalField(
            epc.fields.coordsField('content.location.initial.coords', 'Location'),
            'content.location.initial.type',
            (value) => value !== InitialMapLocation.Selection,
          ),
          epc.fields.sliderField('content.location.zoom', 'Zoom level', {
            units: [{ min: 1, max: 21, step: 1 }],
          }),
          epc.utils.separator(),
          epc.fields.toggleField('content.options.enableZoom', 'Allow zoom & drag'),
        ],
        {
          hideWhen: (
            _elementId: string,
            _subsectionResolver: IPropEditorResolver,
            props: MapPropsModel,
          ) => !props.content?.input.coordinates,
        },
      ),
      epc.subsections.subsection(
        'content.markers',
        'Markers',
        [
          epc.fields.selectFieldFromList(
            'content.markers.style',
            'Style',
            [
              { label: 'Pin', value: MarkerStyle.Pin },
              { label: 'Bubble', value: MarkerStyle.Bubble },
              { label: 'Image', value: MarkerStyle.Image },
            ],
            { allowNone: false },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.selectFieldFromSchema(
                'content.markers.title',
                'Title',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText', '!image', '!file'),
                  placeholder: 'Optional',
                  allowNone: true,
                },
              ),
              epc.fields.selectFieldFromSchema(
                'content.markers.field',
                'Field',
                'data.?key:value.dataSource',
                {
                  valueField: COLUMN_ID_OR_NAME,
                  hideLabel: false,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText', '!image', '!file'),
                  placeholder: 'Optional',
                  allowNone: true,
                },
              ),
              epc.utils.conditionalField(
                epc.fields.selectFieldFromSchema(
                  'content.markers.image',
                  'Image/Icon',
                  'data.?key:value.dataSource',
                  {
                    valueField: COLUMN_ID_OR_NAME,
                    hideLabel: false,
                    placeholder: 'Optional',
                    allowNone: true,
                  },
                ),
                'content.markers.style',
                (style: MarkerStyle) => style === MarkerStyle.Pin,
              ),
              ...epc.utils.conditionalFields(
                [
                  epc.utils.separator(),
                  epc.fields.listCustomFieldsField(
                    'content.markers.customFields',
                    'Add Custom Fields',
                    {
                      withLabel: false,
                      withLabelPosition: false,
                      source: 'schema',
                    },
                  ),
                ],
                'content.markers.style',
                (style: MarkerStyle) => style !== MarkerStyle.Bubble,
              ),
              epc.fields.toggleField('content.markers.clustering', 'Marker clustering'),
            ],
            'data.?key:value.dataSource',
            (value) => !value,
          ),
        ],
        {
          hideWhen: (
            _elementId: string,
            _subsectionResolver: IPropEditorResolver,
            props: MapPropsModel,
          ) => !props.content?.input.coordinates,
        },
      ),
      epc.subsections.subsection(
        'content.card',
        'Card',
        [
          epc.fields.selectFieldFromSchema(
            'content.card.title',
            'Title',
            'data.?key:value.dataSource',
            {
              valueField: COLUMN_ID_OR_NAME,
              hideLabel: false,
              optionsFilter: epc.utils.columnFormatFilter('!secureText', '!image', '!file'),
            },
          ),
          epc.fields.selectFieldFromSchema(
            'content.card.description',
            'Description',
            'data.?key:value.dataSource',
            {
              valueField: COLUMN_ID_OR_NAME,
              hideLabel: false,
              optionsFilter: epc.utils.columnFormatFilter('!secureText', '!image', '!file'),
              placeholder: 'Optional',
              allowNone: true,
            },
          ),
          epc.fields.selectFieldFromSchema(
            'content.card.image',
            'Image/icon',
            'data.?key:value.dataSource',
            {
              valueField: COLUMN_ID_OR_NAME,
              hideLabel: false,
              placeholder: 'Optional',
              allowNone: true,
            },
          ),
          epc.utils.separatedField(
            epc.fields.listCustomFieldsField('content.card.customFields', 'Add Custom Fields', {
              withLabel: true,
              withLabelPosition: true,
              source: 'schema',
            }),
          ),
          epc.utils.separatedField(
            epc.fields.listItemActionsField('content.card.actions', 'Additional Actions'),
          ),
        ],
        {
          optional: true,
          hideWhen: (
            _elementId: string,
            _subsectionResolver: IPropEditorResolver,
            props: MapPropsModel,
          ) => !props.content?.input.coordinates,
        },
      ),
      epc.subsections.searchAndFiltersSubsection(),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.map', 'Padding & Alignment', [
          epc.fields.spacingAndAlignField('style.map.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.map', 'Map', [
          epc.fields.colorField('style.map.background', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.radiusField('style.map.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.map.effect'),
        ]),
        epc.subsections.subsection('style.marker', 'Marker', [
          epc.fields.radiosField(
            'style.marker.selectedStyle',
            '',
            [
              { label: 'Default', value: 'default' },
              { label: 'Selected', value: 'selected' },
            ],
            { hideLabel: true, defaultValue: 'default' },
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.marker.default.fill', 'Fill', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.marker.default.outline', 'Outline', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.marker.default.label', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.utils.conditionalField(
                epc.fields.colorField('style.marker.default.labelBackground', 'Label background', {
                  allowGradient: false,
                  allowNone: false,
                }),
                'content.markers.style',
                (style: MarkerStyle) => style === MarkerStyle.Bubble,
              ),
            ],
            'style.marker.selectedStyle',
            (value) => value !== 'default',
          ),
          ...epc.utils.conditionalFields(
            [
              epc.fields.colorField('style.marker.selected.fill', 'Fill', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.marker.selected.outline', 'Outline', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.fields.colorField('style.marker.selected.label', 'Label', {
                allowGradient: false,
                allowNone: false,
              }),
              epc.utils.conditionalField(
                epc.fields.colorField('style.marker.selected.labelBackground', 'Label background', {
                  allowGradient: false,
                  allowNone: false,
                }),
                'content.markers.style',
                (style: MarkerStyle) => style === MarkerStyle.Bubble,
              ),
            ],
            'style.marker.selectedStyle',
            (value) => value !== 'selected',
          ),
        ]),
        epc.subsections.subsection('style.card', 'Card', [
          epc.fields.colorField('style.card.title', 'Title', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.description', 'Description', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.links', 'Links', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.background', 'Background', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.divider', 'Divider', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.buttonFill', 'Button fill', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.card.buttonLabel', 'Button label', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.radiusField('style.card.radius', 'Radius'),
          ...epc.fields.visualEffectField('style.card.effect'),
        ]),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.map.title',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}

function getInitials(): PartialPropsOf<'map'> {
  return {
    content: {
      searchAndFilters: { injectToElement: true },
      markers: {
        style: MarkerStyle.Pin,
        customFields: [],
      },
      card: {
        enabled: false,
        customFields: [],
        actions: [],
      },
    },
    style: {
      marker: {
        selectedStyle: 'default',
      },
    },
  };
}

function getDefaults(): PropsOf<'map'> {
  return {
    content: {
      searchAndFilters: {
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      columns: [],
      title: {
        enabled: true,
        label: 'Map',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      markers: {
        style: MarkerStyle.Pin,
        customFields: [],
        clustering: true,
      },
      card: {
        enabled: false,
        customFields: [],
        actions: [],
      },
      location: {
        initial: {
          type: InitialMapLocation.Selection,
          coords: DEFAULT_MAP_COORDS,
        },
        zoom: 4,
      },
      options: {
        type: MapType.Road,
        style: MapStyle.Light,
        enableZoom: true,
        showAllMarkers: true,
      },
      input: {},
    },
    style: {
      map: {
        title: {
          color1: '$t.title',
          selectorType: 'solid',
        },
        background: {
          color1: '$t.fill',
          selectorType: 'solid',
        },
        radius: '$t.radius',
        offset: {
          margin: '$t.spacingExternal',
          padding: '$t.spacingInternal',
        },
        effect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      marker: {
        selectedStyle: 'default',
        default: {
          fill: {
            color1: '$t.accent',
            selectorType: 'solid',
          },
          outline: {
            color1: '$t.canvas',
            selectorType: 'solid',
          },
          label: {
            color1: '$t.title',
            selectorType: 'solid',
          },
          labelBackground: {
            color1: '$t.canvas',
            selectorType: 'solid',
          },
        },
        selected: {
          fill: {
            color1: '$t.accent',
            selectorType: 'solid',
          },
          outline: {
            color1: '$t.canvas',
            selectorType: 'solid',
          },
          label: {
            color1: '$t.accentLabel',
            selectorType: 'solid',
          },
          labelBackground: {
            color1: '$t.accent',
            selectorType: 'solid',
          },
        },
      },
      card: {
        title: {
          color1: '$t.title',
          selectorType: 'solid',
        },
        description: {
          color1: '$t.value',
          selectorType: 'solid',
        },
        links: {
          color1: '$t.accent',
          selectorType: 'solid',
        },
        background: {
          color1: '$t.canvas',
          selectorType: 'solid',
        },
        divider: {
          color1: '$t.outline',
          selectorType: 'solid',
        },
        buttonFill: {
          color1: '$t.accent',
          selectorType: 'solid',
        },
        buttonLabel: {
          color1: '$t.accentLabel',
          selectorType: 'solid',
        },
        radius: '$t.radius',
        effect: {
          type: 'drop-shadow',
          data: { x: '1', y: '1', blur: '6', color1: '#00000014' },
        },
      },
      container: {
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
      },
    },
  } as PropsOf<'map'>;
}
